import useFuseSearch from '@exodus/react-hooks/common/useFuseSearch';
import Box from '@passkeys/ui/box';
import Button from '@passkeys/ui/button';
import Checkbox from '@passkeys/ui/checkbox';
import Icon from '@passkeys/ui/icon';
import Text from '@passkeys/ui/text';
import TextField from '@passkeys/ui/text-field';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import ScrollableList from '@/ui/components/scrollable-list';
import { ALL_OPTION } from '@/ui/constants';
import type { AllOption } from '@/ui/types';
import { cn } from '@/ui/utils/classnames';
import { isAllOption } from '@/ui/utils/content';
import AllOptionItem from './parts/all-option-item.js';

interface PickerModalPropsBase<Option> {
  options: Option[];
  renderOption: (option: Option) => ReactNode;
  getOptionKey: (option: Option) => string;
  noSearch?: boolean;
}

interface PickerModalPropsSingle<Option> extends PickerModalPropsBase<Option> {
  multiple?: false;
  includeAllOption?: false;
  onClose: (selected?: Option) => void;
  defaultSelected?: [Option];
}

interface PickerModalPropsSingleWithAll<Option>
  extends PickerModalPropsBase<Option> {
  multiple?: false;
  includeAllOption: true;
  onClose: (selected?: Option | AllOption) => void;
  defaultSelected?: [Option];
}

interface PickerModalPropsMultiple<Option>
  extends PickerModalPropsBase<Option> {
  multiple: true;
  includeAllOption?: false;
  onClose: (selected?: Option[]) => void;
  defaultSelected?: Option[];
}

interface PickerModalPropsMultipleWithAll<Option>
  extends PickerModalPropsBase<Option> {
  multiple: true;
  includeAllOption: true;
  onClose: (selected?: Option[] | AllOption) => void;
  defaultSelected?: Option[];
}

export type PickerModalProps<Option> =
  | PickerModalPropsSingle<Option>
  | PickerModalPropsSingleWithAll<Option>
  | PickerModalPropsMultiple<Option>
  | PickerModalPropsMultipleWithAll<Option>;

function PickerModal<Option>({
  options,
  renderOption,
  getOptionKey,
  onClose,
  defaultSelected,
  includeAllOption = false,
  multiple = false,
  noSearch = false,
}: PickerModalProps<Option>) {
  const [selected, setSelected] = useState(new Set(defaultSelected));

  const [searchQuery, setSearchQuery] = useState('');

  const handleDoneClick = () => {
    const didSelectAll = selected.size === options.length;
    if (didSelectAll) {
      (onClose as (selected: AllOption) => void)(ALL_OPTION);
      return;
    }

    (onClose as (selected: Option[]) => void)([...selected]);
  };

  const handleOptionClick = (option: Option | AllOption) => {
    if (isAllOption(option)) {
      (onClose as (selected: AllOption) => void)(option);
      return;
    }

    if (!multiple) {
      (onClose as (selected: Option) => void)(option);
      return;
    }

    setSelected((previousSelected) => {
      const newSelected = new Set(previousSelected);
      if (newSelected.has(option)) {
        newSelected.delete(option);
      } else {
        newSelected.add(option);
      }

      return newSelected;
    });
  };

  const renderOptionItem = useCallback(
    (option: Option | AllOption) => {
      const left = isAllOption(option) ? (
        <AllOptionItem />
      ) : (
        renderOption(option)
      );
      const right =
        isAllOption(option) || (!multiple && !defaultSelected) ? (
          <Icon
            className="text-black/40 theme-dark:text-neutral-400"
            name="caret-right"
            size={16}
          />
        ) : (
          <Checkbox
            defaultChecked={selected.has(option)}
            dataTestId="picker-checkbox"
          />
        );

      return (
        <div className="flex w-full items-center justify-between gap-x-2 py-4">
          {left}
          {right}
        </div>
      );
    },
    [defaultSelected, multiple, renderOption, selected],
  );

  const getOptionItemKey = (option: Option | AllOption) =>
    isAllOption(option) ? 'all' : getOptionKey(option);

  // This will return all options if the query is empty.
  const searchedOptions = useFuseSearch(options, searchQuery, {
    keys: [
      'name',
      'displayName',
      'ticker',
      'displayTicker',
      'displayNetworkName',
    ],
    threshold: 0.1,
    shouldSort: false,
  });

  const revisedOptions: (Option | AllOption)[] = includeAllOption
    ? [ALL_OPTION, ...searchedOptions]
    : searchedOptions;

  return (
    <div
      className={cn(
        'grid h-full pb-8',
        noSearch ? 'grid-rows-[1fr]' : 'grid-rows-[64px_1fr]',
      )}
    >
      {!noSearch && (
        <TextField
          value={searchQuery}
          className="w-full"
          placeholder="Search name or symbol"
          prefix={
            <Icon
              className="text-gray-500 theme-dark:text-neutral-200"
              name="magnifying-glass"
            />
          }
          dataTestId="picker-search-input"
          onChange={setSearchQuery}
        />
      )}

      {revisedOptions.length === 0 && (
        <Text
          align="center"
          size={14}
          className="pt-40 text-black/50 theme-dark:text-white/50"
        >
          No results found.
        </Text>
      )}

      {revisedOptions.length > 0 && (
        <Box className="flex h-fit max-h-full w-full flex-col items-center overflow-hidden px-6">
          <ScrollableList<Option | AllOption>
            items={revisedOptions}
            renderItem={renderOptionItem}
            getItemKey={getOptionItemKey}
            getClickHandler={(option: Option | AllOption) => () =>
              handleOptionClick(option)
            }
            className="py-2"
            forceBorder
          />
        </Box>
      )}

      {multiple && (
        <Button
          className="mx-4 mt-8"
          onClick={handleDoneClick}
          disabled={selected.size === 0}
          size="large"
          dataTestId="picker-done-button"
        >
          Done
        </Button>
      )}
    </div>
  );
}

export default PickerModal;
